<template>
  <div class="newob__container">
    <div class="newob__container-left__section d-flex flex-column">
      <div class="left__section-container d-flex flex-column flex-fill">
        <div class="d-flex flex-column justify-content-start">
          <div class="left__section-header d-flex flex-row justify-content-between align-items-center">
            <div class="logo">
              <img :src="require('../assets/logo.svg')" />
            </div>
          </div>
          <div class="left__section-body">
            <div class="body__title title text-lightmode mb-2">{{ $t("newonboarding.welcome.screen1.title") }}</div>
            <Target />
          </div>
        </div>

        <div class="left__section-footer">
          <div class="left__section-footer__action">
            <ActionButton :label="actionLabel" :isLoading="loading" :disabled="isDisabledAction" @onClick="onCreateNewPage" />
          </div>
          <div class="typo-title2 typo-medium text-mono3 text-center mt-2 clickable d-flex align-items-center justify-content-center" @click="onTryEditor">
            <span v-if="loadingTryEditor" class="snack-loading mr-1">
              <i class="icon icon-loading"></i>
            </span>
            <span class="left__section-footer__try-editor">{{ $t("newonboarding.welcome.screen1.tryOurEditor") }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="newob__container-right__section" id="right-section">
      <div class="illustration d-flex flex-column align-items-center justify-content-center">
        <img :src="illustrationSrc" :key="illustrationSrc" class="illustration__image mb-3 fade-in" />
        <div class="illustration__desc d-flex flex-column align-items-center">
          <div class="top">{{ illustrationDesc }}</div>
          <div class="bottom">{{ $t("newonboarding.welcome.screen1.withGempages") }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState, mapMutations, mapActions} from "vuex";
import Target from './Targets.vue';
import ActionButton from '../components/ActionButton.vue';
import { mutations, actions } from "@/store/newonboarding/types.js";
import newObsLocalData from "@/utils/newObsLocalData.js";
import { generatePageTitle } from "@/store/page/actions";
import { transformPageNameToHandleType } from "@/store/newonboarding/actions";
import { createThemeAndPageV7 } from "@/modules/Instant/utils";
import initGemPagesV7 from "@/mixins/initGemPagesV7.js";

export default {
  name: "BaseLayout",
  components: {
    Target,
    ActionButton
  },
  mixins: [initGemPagesV7],
  data() {
    return {
      endStep: 2,
      loading: false,
      loadingTryEditor: false,
      newPageData: "",
    };
  },
  computed: {
    ...mapState({
      shopInfo: state => state.account.shopInfo
    }),
    ...mapState("newonboarding", {
      currentStep: state => state.welcome.currentStep,
      selectedPageObjective: state => state.welcome.data.pageObjective,
      hoverPageObjective: state => state.welcome.hoverPageObjective,
      othersObjective: state => state.othersObjective,
      industries: state => state.welcome.data.industries,
      isDisabledBrowseTemplate: state => state.welcome.isDisabledBrowseTemplate
    }),
    actionLabel() {
      return this.$t("newonboarding.welcome.screen1.button");
    },
    stepIndicator() {
      return `${this.currentStep} of ${this.endStep}`;
    },
    isDisabledAction() {
      if (this.currentStep == 1) return this.selectedPageObjective === "" || this.loadingTryEditor;
      return this.industries.length === 0 || (this.industries.length > 0 && this.isDisabledBrowseTemplate);
    },
    illustrationName() {
      let name = this.selectedPageObjective;
      if (this.hoverPageObjective !== "") {
        name = this.hoverPageObjective;
      }
      return name == "" ? "starter" : name;
    },
    illustrationSrc() {
      return require(`../assets/Illustrations/${this.illustrationName}.svg`);
    },
    illustrationDesc() {
      let pageName = this.selectedPageObjective;
      if (this.hoverPageObjective !== "") {
        pageName = this.hoverPageObjective;
      }
      if (pageName === "") return this.$t("newonboarding.welcome.screen1.paragraph2");
      return `${this.$t("newonboarding.welcome.screen1.paragraph1")} ${this.illustrationText(pageName)}`;
    },
    isGemPagesV6() {
      return (this && this.shopInfo && this.shopInfo.shopType.isGemPagesV6) || window.___gemShopInfo.shopType.isGemPagesV6 || false
    }
  },
  created() {
    this.checkStatusOBS();
    this.checkCurrentState();
  },
  methods: {
    ...mapMutations("newonboarding", {
      gotoStep: mutations.OBS_GO_TO_STEP,
      obsAddIndustry: mutations.OBS_ADD_INDUSTRY,
      obsReplaceIndustry: mutations.OBS_REPLACE_INDUSTRY,
      obsPageObjective: mutations.OBS_PAGE_OBJECTIVE,
      obsResetData: mutations.OBS_RESET_DATA,
    }),
    ...mapActions("page", {
      createPageWithOneTap: "createPageWithOneTap",
      newPage: "newPage"
    }),
    ...mapActions("account", {
      whoAmI: "accountWhoAmI"
    }),
    ...mapActions("newonboarding", {
      updateSurveyOnboarding: actions.UPDATE_SURVEY_ONBOARDING,
    }),
    mutationData(data) {
      return {
        data: data,
        shopifyDomain: this.shopInfo && this.shopInfo.shopifyDomain
      }
    },
    handleAction() {
      if (this.currentStep === 1) this.gotoStep(this.mutationData(2));
    },
    previousStep() {
      this.gotoStep(this.mutationData(1));
    },
    checkCurrentState() {
      const shopifyDomain = this.shopInfo && this.shopInfo.shopifyDomain;
      if (newObsLocalData.check(shopifyDomain)) {
        const lastSurvey = newObsLocalData.get(shopifyDomain);
        if (!lastSurvey || !lastSurvey.store) return;
        if (lastSurvey.store.currentStep) {
          this.gotoStep({ data: lastSurvey.store.currentStep, shopifyDomain: this.shopInfo && this.shopInfo.shopifyDomain });
        } else {
          newObsLocalData.set(1, "currentStep", shopifyDomain);
        }
        if (lastSurvey.store.industries) {
          const localIndustries = lastSurvey.store.industries;
          const length = localIndustries.length;
          if (length == 0) return;
          this.obsReplaceIndustry({ data: localIndustries, shopifyDomain: this.shopInfo && this.shopInfo.shopifyDomain });
        }
        if (lastSurvey.store.objective) {
          this.obsPageObjective({ data: lastSurvey.store.objective, shopifyDomain: this.shopInfo && this.shopInfo.shopifyDomain });
        }
      }
    },
    checkStatusOBS() {
      const surveys = this.shopInfo && this.shopInfo.surveys;
      const shopifyDomain = this.shopInfo && this.shopInfo.shopifyDomain;

      if (!surveys) {
        if (newObsLocalData.check(shopifyDomain)) {
          const survey = newObsLocalData.get(shopifyDomain);
          if (survey && survey.store && survey.store.doneSurvey) {
            this.$router.push({name: "Dashboard"});
            return;
          }
        }
      } else {
        const surveyObs = surveys.filter(sv => sv.handle == "onboarding") || [];
        const newSurveyObs = surveys.filter(sv => sv.handle == "onboarding-v2") || [];
        if ((surveyObs && surveyObs.length > 0) || (newSurveyObs && newSurveyObs.length > 0)) {
          newObsLocalData.remove(shopifyDomain);
          this.$router.push({ name: "Dashboard" });
        }
      }
    },
    actionLabelType(type) {
      switch (type) {
        case "Complete Store":
          return this.$t("newonboarding.welcome.screen2.complatestore");
        case "Landing Page":
          return this.$t("newonboarding.welcome.screen2.newladdingpage");
        case "Product Page":
          return this.$t("newonboarding.welcome.screen2.newproductpage");
        case "Home Page":
          return this.$t("newonboarding.welcome.screen2.newhomepages");
        case "Collection Page":
          return this.$t("newonboarding.welcome.screen2.newcollectionpages");
        case "Blog Post":
          return this.$t("newonboarding.welcome.screen2.newarticlepages");
        default:
          break;
      }
    },
    illustrationText(type) {
      switch (type) {
        case "Complete Store":
          return this.$t("newonboarding.welcome.option.completed.title");
        case "Landing Page":
          return this.$t("onboarding.landingpages.title");
        case "Product Page":
          return this.$t("onboarding.productpages.title");
        case "Home Page":
          return this.$t("onboarding.homepages.title");
        case "Collection Page":
          return this.$t("onboarding.collectionpages.title");
        case "Blog Post":
          return this.$t("newonboarding.welcome.option.blog.title");
        default:
          break;
      }
    },
    getNewPageType(type) {
      switch (type) {
        case "Complete Store":
          return "subpages";
        case "Landing Page":
          return "subpages";
        case "Product Page":
          return "productpages";
        case "Home Page":
          return "homepages";
        case "Collection Page":
          return "collectionpages";
        case "Blog Post":
          return "articlepages";
        default:
          break;
      }
      return null;
    },
    onCreateNewPage() {
      if (this.isDisabledAction || this.loading) return;
      this.loading = true;
      this.onDoneSurvey();
      this.saveSurveyOnboarding();
      const type = this.getNewPageType(this.selectedPageObjective);
      this.createPage(type);
    },
    onTryEditor() {
      if (this.loadingTryEditor) return;
      this.loadingTryEditor = true;
      this.obsResetData();
      this.onDoneSurvey();
      newObsLocalData.set('Landing Page', "objective", this.shopInfo?.shopifyDomain)
      this.saveSurveyOnboarding({ isSkipSurvey: true });
      this.createPage('subpages');
    },
    onDoneSurvey() {
      const shopifyDomain = this.shopInfo && this.shopInfo.shopifyDomain;
      newObsLocalData.set(true, "doneSurvey", shopifyDomain);
    },
    async createPage(pageType) {
      if (this.isGemPagesV6) {
        // V6
        await this.newPage({
          params: {
            templateId: 0,
            pageLayout: "default",
            pageTitle: generatePageTitle(pageType)
          },
          type: pageType,
          onSuccess: data => {
            const originURL = window.location.origin;
            const href = new URL(originURL + data.editUrl + '&explore-template=true');
            this.loading = false;
            this.loadingTryEditor = false;
            window.location.href = href;
          },
          onError: () => {
            this.loading = false;
            this.loadingTryEditor = false;
            this.$router.push({ name: "Dashboard" });
          }
        });
      } else {
        // V7
        try {
          await this.connectGemPagesV7()
          await createThemeAndPageV7({ pageType, isOnboarding: true })
        } catch(e) {
          this.loading = false;
          this.loadingTryEditor = false;
          this.$router.push({ name: "Dashboard" });
          // eslint-disable-next-line no-console
          console.error(e);
        }
        return;
      }
    },
    saveSurveyOnboarding (args) {
      let params = {
        "pageType": args?.isSkipSurvey ? 'template-page' : transformPageNameToHandleType(this.selectedPageObjective),
        "industry": this.industries,
        "skipped": args?.isSkipSurvey,
      };
      this.updateSurveyOnboarding(params).then((res) => {
        if (res.data && res.data.message == "success") {
          newObsLocalData.removeAll();
        }
      });
      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
.end-image {
  width: 100%;
  height: 1px;
}

.newob__container {
  display: grid;
  grid-template-columns: 50vw 50vw;
  min-height: 100vh;

  &-left__section {
    background-color: $light1;
    padding: 40px 40px 0px 40px;
  }

  &-right__section {
    background-color: $mono3;
  }
}

.template-layout {
  background-color: $light3 !important;
  padding: 0px 16px;
  overflow: hidden;
  position: relative;
}

.image__container {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  padding: 0px 16px;
  overflow: hidden;
  white-space: nowrap;
  animation: bannermove 80s linear infinite;
}

@keyframes bannermove {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(0, -50%);
  }
}

.left__section-container {
  width: 424px;
  align-self: center;

  .left__section-footer {
    margin-top: 101px;

    &-step2 {
      margin-bottom: 204px !important;
    }

    &__desc {
      font-size: 12px;
      line-height: 18px;

      .term,
      .policy {
        color: $dark6;
        text-decoration: underline;

        &:hover {
          color: $textLightModeHigh;
        }
      }
    }

    &__try-editor {
      line-height: 24px !important;
    }
  }

  .left__section-body {
    .body__description {
      font-size: 12px;
      line-height: 18px;
    }
  }
}

.left__section-header {
  margin-bottom: 64px;
}

.body {
  &__title {
    font-size: 20px;
    line-height: 30px;
    font-weight: 600;
  }
}

.backstep {
  cursor: pointer;

  .icon {
    font-size: 16px;
  }

  &:hover {
    color: $textLightModeHigh;
  }
}

.illustration {
  height: 100%;
  background-color: $mono3;

  &__image {
    max-width: 600px;
    max-height: 900px;
    margin: 0px 40px;
  }

  &__desc {
    font-size: 18px;
    line-height: 27px;
    font-weight: 600;
    color: $light2;
  }
}

.start-image {
  width: 100%;
  display: block;
  padding: 0px;
  margin: 0px;
}

.fade-in {
  opacity: 1;
  animation-name: fadeInOpacity;
  animation-iteration-count: 1;
  animation-timing-function: ease-in;
  animation-duration: 0.45s;
}

@keyframes fadeInOpacity {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
